import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    MutableRefObject
} from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

type CardProps = {
    label?: string;
    title: string;
    body: string;
    contentSrc: string;
};

const IMAGE_CYCLE_TIME = 7000;
const hexAlpha30 = "4D"; // 30% alpha as hex

type FeatureHighlightColor =
    | "lightblue"
    | "white"
    | "darkblue"
    | "blue"
    | "purple";

interface FeatureHighlightStyle {
    backgroundColor: string;
    textColor: string;
    labelColor: string;
    headerColor: string;
    subtitleColor: string;
    cardActiveBackgroundColor: string;
    cardHoverBackgroundColor: string;
}

const FeatureHighlightStyles: Record<
    FeatureHighlightColor,
    FeatureHighlightStyle
> = {
    lightblue: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#deebff",
        cardActiveBackgroundColor: wacToken("color.neutral.light0"),
        cardHoverBackgroundColor: wacToken("color.neutral.light0"),
        // eslint-disable-next-line local/ensure-wac-token-usage
        headerColor: "#253858",
        // eslint-disable-next-line local/ensure-wac-token-usage
        subtitleColor: "#253858",
        // eslint-disable-next-line local/ensure-wac-token-usage
        textColor: "#091e42",
        // eslint-disable-next-line local/ensure-wac-token-usage
        labelColor: "#505f79"
    },
    white: {
        backgroundColor: wacToken("color.neutral.light0"),
        cardActiveBackgroundColor: wacToken("color.neutral.light0"),
        cardHoverBackgroundColor: wacToken("color.neutral.light0"),
        // eslint-disable-next-line local/ensure-wac-token-usage
        headerColor: "#253858",
        // eslint-disable-next-line local/ensure-wac-token-usage
        subtitleColor: "#253858",
        // eslint-disable-next-line local/ensure-wac-token-usage
        textColor: "#091e42",
        // eslint-disable-next-line local/ensure-wac-token-usage
        labelColor: "#505f79"
    },
    darkblue: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#091e42",
        cardActiveBackgroundColor: wacToken("color.neutral.light0"),
        cardHoverBackgroundColor: wacToken("color.neutral.light0"),
        headerColor: wacToken("color.neutral.light0"),
        subtitleColor: wacToken("color.neutral.light0"),
        textColor: wacToken("color.neutral.light0"),
        labelColor: wacToken("color.neutral.light0")
    },
    blue: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#0049b0",
        cardActiveBackgroundColor: wacToken("color.neutral.light0"),
        cardHoverBackgroundColor: wacToken("color.neutral.light0"),
        headerColor: wacToken("color.neutral.light0"),
        subtitleColor: wacToken("color.neutral.light0"),
        textColor: wacToken("color.neutral.light0"),
        labelColor: wacToken("color.neutral.light0")
    },
    purple: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#403294",
        cardActiveBackgroundColor: wacToken("color.neutral.light0"),
        cardHoverBackgroundColor: wacToken("color.neutral.light0"),
        headerColor: wacToken("color.neutral.light0"),
        subtitleColor: wacToken("color.neutral.light0"),
        textColor: wacToken("color.neutral.light0"),
        labelColor: wacToken("color.neutral.light0")
    }
};

type FeatureSectionProps = {
    subtitle?: string;
    header?: string;
    cardLayout?: "left" | "right";
    cardVerticalLayout?: "above" | "below";
    background: FeatureHighlightColor;
    barColor?:
        | "#ffc400"
        | "#ff7452"
        | "#00c7e5"
        | "#57d9a3"
        | "#2684ff"
        | "#0052cc"
        | "#b2d4ff"
        | "#998dd9";
    transparentActiveBackground?: boolean;
    transparentBackground?: boolean;
    /**
     * Defaults to video
     */
    mediaType?: "video" | "image";
    cards: CardProps[];
};

const wrapperCss = css({
    height: "fit-content",
    margin: "0 auto",
    maxWidth: "1200px",
    padding: `0 ${token("space.200")}`
});

const rowCss = css({
    display: "flex",
    gap: token("space.200"),
    [media.below.md]: {
        flexDirection: "column"
    }
});

const columnReverseCss = css({
    display: "flex",
    gap: token("space.200"),
    [media.below.md]: {
        flexDirection: "column-reverse"
    }
});

const contentsWrapperCss = css({
    flex: 2,
    overflow: "hidden"
});

type FeatureHighlightImageProps = {
    card: CardProps;
};

export const FeatureHighlightImage = (props: FeatureHighlightImageProps) => {
    const { card } = props;
    return (
        // eslint-disable-next-line no-restricted-syntax
        <img
            src={card.contentSrc}
            alt={card.title}
            width="100%"
            height="auto"
        />
    );
};

type FeatureHighlightVideoProps = {
    key: string;
    index: number;
    card: CardProps;
    onEnded: () => void;
};

const isMutableRef = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    val: any
): val is MutableRefObject<HTMLVideoElement[]> => {
    return val && val.current;
};

export const FeatureHighlightVideo = React.forwardRef<
    HTMLVideoElement[],
    FeatureHighlightVideoProps
>((props, ref) => {
    const { key, index, card, onEnded } = props;
    return (
        <video
            key={key}
            ref={(element) => {
                if (ref && isMutableRef(ref) && element) {
                    ref.current[index] = element;
                }
            }}
            width="100%"
            height="100%"
            muted={true}
            // eslint-disable-next-line react/no-unknown-property
            object-fit="fill"
            preload="auto"
            tabIndex={-1}
            onEnded={onEnded}
            playsInline={true}
        >
            <source src={card.contentSrc} />
        </video>
    );
});

const isFeatureHighlightColor = (
    color: string
): color is FeatureHighlightColor => {
    return (
        color === "lightblue" ||
        color === "white" ||
        color === "darkblue" ||
        color === "blue" ||
        color === "purple"
    );
};

const getFeatureHighlightColor = (
    color: string | undefined
): FeatureHighlightColor => {
    const featureHighlightColor = color?.toLowerCase();
    if (
        featureHighlightColor &&
        isFeatureHighlightColor(featureHighlightColor)
    ) {
        return featureHighlightColor;
    }

    return "darkblue";
};

const getFeatureHighlightStyle = (color: string | undefined) => {
    const featureHighlightColor = getFeatureHighlightColor(color);
    return FeatureHighlightStyles[featureHighlightColor];
};

// eslint-disable-next-line complexity
const FeatureHighlight = (props: FeatureSectionProps) => {
    const {
        transparentBackground,
        background,
        barColor,
        mediaType = "video",
        transparentActiveBackground
    } = props;
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [isVideoInView, setIsVideoInView] = useState<boolean>(false);
    const timeoutRef: MutableRefObject<number | null> = useRef(null);

    const {
        backgroundColor,
        textColor,
        labelColor,
        headerColor,
        subtitleColor,
        cardActiveBackgroundColor,
        cardHoverBackgroundColor
    } = getFeatureHighlightStyle(background);

    const videoRefMap = useRef<HTMLVideoElement[]>(
        Array(props.cards.length).fill(null)
    );

    const videoWrapperRef = useRef(null);

    const sectionCss = css({
        backgroundColor: transparentBackground
            ? "transparent"
            : `${backgroundColor}`,
        overflow: "hidden"
    });

    const subtitleCss = css({
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        color: `${subtitleColor}`,
        letterSpacing: "1px",
        margin: "0 auto",
        marginBlockEnd: "10px",
        textTransform: "uppercase",
        fontWeight: "400",

        [media.only.sm]: {
            padding: "1rem",
            paddingBlockStart: token("space.400"),
            fontSize: "0.75rem",
            lineHeight: "2"
        },

        [media.below.sm]: {
            padding: "1rem",
            paddingBlockStart: token("space.400"),
            fontSize: token("space.300"),
            lineHeight: token("space.400"),
            maxWidth: "max-content"
        }
    });

    const headerCss = css({
        color: `${headerColor}`,
        fontSize: "36px",
        fontWeight: 500,
        lineHeight: "44px",
        margin: "0 auto",
        maxWidth: `calc(66.6666% - ${token("space.200")})`,
        paddingBlockEnd: token("space.400"),
        paddingBlockStart: token("space.800"),
        textAlign: "center",
        [media.below.md]: {
            paddingBlockStart: token("space.400")
        },
        [media.below.sm]: {
            fontSize: token("space.300"),
            lineHeight: token("space.400"),
            maxWidth: "max-content",
            paddingBlockStart: token("space.400")
        }
    });

    const cardsWrapperCss = css({
        flex: 1,
        [media.only.sm]: {
            display: "flex",
            gap: token("space.400")
        },
        [media.below.sm]: {
            display: "inline-flex",
            overflow: "visible",
            transition: "transform 0.3s",
            transform: `translateX(-${selectedIndex * 100}%)`,
            whiteSpace: "nowrap"
        }
    });

    const cardCss = css({
        "display": "inline-flex",
        "flexDirection": "column",
        "flexGrow": 1,
        "appearance": "none",
        "border": 0,
        "backgroundColor": "transparent",
        "borderInlineStart": barColor
            ? `${token("space.100")} solid transparent`
            : "none",
        "color": `${textColor}`,
        "cursor": "pointer",
        "fontFamily": "var(--bxp-charlie-text-font-family)",
        "marginBlockEnd": token("space.100"),
        "padding": token("space.200"),
        "textAlign": "left",
        "width": "100%",
        [media.below.sm]: {
            borderInlineStart: "none",
            whiteSpace: "normal",
            minWidth: "100%",
            borderBlockStart: barColor
                ? `${token("space.100")} solid transparent`
                : "none"
        },
        [media.only.sm]: {
            borderInlineStart: "none",
            borderBlockStart: barColor
                ? `${token("space.100")} solid transparent`
                : "none",
            margin: 0
        },
        "&:hover": {
            backgroundColor: `${cardHoverBackgroundColor + hexAlpha30}`,
            borderInlineStart: barColor
                ? `${token("space.100")} solid ${barColor}`
                : "none",
            [media.below.md]: {
                borderInlineStart: "none",
                borderBlockStart: barColor
                    ? `${token("space.100")} solid ${barColor}`
                    : "none"
            }
        },
        // Tech debt, should be fixed
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> h3": {
            color: `${labelColor}`,
            display: "block",
            fontSize: token("space.150"),
            fontWeight: 400,
            lineHeight: "18px",
            paddingBlockEnd: token("space.200"),
            margin: 0,
            textTransform: "uppercase"
        },
        // Tech debt, should be fixed
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> h4": {
            fontFamily: wacToken("font.charlie.text"),
            fontSize: token("space.250"),
            fontWeight: 500,
            lineHeight: token("space.250"),
            letterSpacing: "0.3px",
            margin: 0,
            paddingBlockEnd: token("space.100"),
            [media.below.md]: {
                fontSize: token("space.200")
            }
        },
        // Tech debt, should be fixed
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> p": {
            fontSize: token("space.200"),
            fontWeight: 400,
            lineHeight: token("space.300"),
            margin: 0
        }
    });

    const selectedCardCss = css({
        backgroundColor: transparentActiveBackground
            ? `${cardActiveBackgroundColor + hexAlpha30}`
            : `${cardActiveBackgroundColor}`,
        borderInlineStart: barColor
            ? `${token("space.100")} solid ${barColor}`
            : "none",
        [media.below.md]: {
            borderInlineStart: "none",
            borderBlockStart: barColor
                ? `${token("space.100")} solid ${barColor}`
                : "none"
        }
    });

    const contentsSliderCss = css({
        transform: `translateX(-${selectedIndex * 100}%)`,
        whiteSpace: "nowrap",
        [media.below.sm]: {
            transition: "transform 0.3s"
        }
    });

    const mobileDotsCss = css({
        display: "none",
        [media.below.sm]: {
            "display": "flex",
            "gap": token("space.150"),
            "justifyContent": "center",
            // Tech debt, should be fixed
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "> button": {
                "appearance": "none",
                "backgroundColor": transparentBackground
                    ? "#a5adba"
                    : `${cardActiveBackgroundColor}`,
                "border": 0,
                "borderRadius": "50%",
                "padding": 0,
                "height": token("space.100"),
                "width": token("space.100"),
                "&.active": {
                    backgroundColor: `${textColor}`
                }
            }
        }
    });

    const resetSelectedIndexVideo = useCallback(() => {
        const video = videoRefMap.current[selectedIndex];
        if (video) {
            video.currentTime = 0;
        }
    }, [selectedIndex, videoRefMap]);

    const resetVideoAndSetSelectedIndex = (index: number) => {
        resetSelectedIndexVideo();
        setSelectedIndex(index);
    };

    const nextCard = () => {
        const nextIndex = selectedIndex + 1;
        if (nextIndex === cards.length) {
            return;
        }

        resetVideoAndSetSelectedIndex(nextIndex);
    };

    const previousCard = () => {
        const nextIndex = selectedIndex - 1;
        if (nextIndex === -1) {
            return;
        }

        resetVideoAndSetSelectedIndex(nextIndex);
    };

    const cards = props.cards.map((card, index) => (
        <button
            className="button-class"
            key={`${card.title}-${index}-button`}
            css={[cardCss, selectedIndex === index && selectedCardCss]}
            onClick={() => {
                if (index !== selectedIndex)
                    resetVideoAndSetSelectedIndex(index);
            }}
            tabIndex={0}
        >
            {card.label && <h3>{card.label}</h3>}
            <h4>{card.title}</h4>
            <p>{card.body}</p>
        </button>
    ));

    const contents = () => {
        if (mediaType === "image") {
            return props.cards.map((card, index) => {
                return (
                    <FeatureHighlightImage
                        key={`${card.title}-${index}-content`}
                        card={card}
                    />
                );
            });
        }

        return props.cards.map((card, index) => {
            return (
                <FeatureHighlightVideo
                    key={`${card.title}-${index}-content`}
                    index={index}
                    card={card}
                    ref={videoRefMap}
                    onEnded={() => {
                        selectedIndex + 1 === cards.length
                            ? resetVideoAndSetSelectedIndex(0)
                            : nextCard();
                    }}
                />
            );
        });
    };

    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);

    const startDrag = (
        touchEvent?: React.TouchEvent,
        mouseEvent?: React.MouseEvent
    ) => {
        setIsDragging(true);
        setStartX(
            (touchEvent && touchEvent.touches[0]
                ? touchEvent?.touches[0].clientX
                : mouseEvent?.clientX) || 0
        );
    };

    const stopDrag = (
        touchEvent?: React.TouchEvent,
        mouseEvent?: React.MouseEvent
    ) => {
        if (isDragging) {
            setIsDragging(false);

            let dragAmount = 0;
            if (touchEvent && touchEvent.changedTouches[0]) {
                dragAmount = touchEvent.changedTouches[0].clientX - startX;
            } else if (mouseEvent) {
                dragAmount = mouseEvent.clientX - startX;
            }

            if (Math.abs(dragAmount) > 100) {
                if (dragAmount < 0) {
                    nextCard(); // Swipe left
                } else {
                    previousCard(); // Swipe right
                }
            }
        }
    };

    useEffect(() => {
        const handleIntersect = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setIsVideoInView(true);
                } else {
                    setIsVideoInView(false);
                    resetSelectedIndexVideo();
                }
            });
        };

        const observer = new IntersectionObserver(handleIntersect, {
            threshold: 0.3
        });

        const ref = videoWrapperRef.current;

        if (ref) {
            observer.observe(ref);
        }

        return () => {
            if (ref) {
                observer.unobserve(ref);
            }
        };
    }, [videoWrapperRef, resetSelectedIndexVideo]);

    useEffect(() => {
        if (isDragging) {
            return;
        }

        // The below logic handles auto playing/pausing videos when they are in/out of view
        // and also handles auto cycling for images

        if (mediaType === "image") {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (!isVideoInView) {
                return;
            }

            timeoutRef.current = window.setTimeout(() => {
                if (selectedIndex + 1 === cards.length) {
                    resetVideoAndSetSelectedIndex(0);
                } else {
                    nextCard();
                }
            }, IMAGE_CYCLE_TIME);
        } else if (mediaType === "video") {
            videoRefMap.current.forEach((video, videoId) => {
                if (!video || !video.play) {
                    return;
                }
                if (videoId === selectedIndex && isVideoInView) {
                    video.play().catch(() => {
                        // Ignore error likely due to autoplay restrictions or browser power saving
                    });
                } else {
                    video.pause();
                }
            });
        }
    }, [selectedIndex, isVideoInView, isDragging, videoRefMap]);

    return (
        <section css={sectionCss}>
            <div className="wrapper-class" css={wrapperCss}>
                {props.subtitle && <h6 css={subtitleCss}>{props.subtitle}</h6>}
                {props.header && <h2 css={headerCss}>{props.header}</h2>}
                {/* Tech debt that should be fixed. See ldr/0012-jsx-a11y-strict.md for details. */}
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                    css={[
                        props.cardVerticalLayout !== "below" && rowCss,
                        props.cardVerticalLayout === "below" && columnReverseCss
                    ]}
                    onTouchStart={(e) => startDrag(e)}
                    onMouseDown={(e) => startDrag(undefined, e)}
                    onTouchEnd={(e) => stopDrag(e)}
                    onMouseUp={(e) => stopDrag(undefined, e)}
                >
                    {props.cardLayout !== "right" && (
                        <div css={cardsWrapperCss}>{cards}</div>
                    )}
                    <div
                        className="image-container"
                        ref={videoWrapperRef}
                        css={contentsWrapperCss}
                    >
                        <div css={contentsSliderCss}>{contents()}</div>
                    </div>
                    {props.cardLayout === "right" && (
                        <div css={cardsWrapperCss}>{cards}</div>
                    )}
                </div>
                <div
                    css={css({
                        padding: `${token("space.400")} 0`
                    })}
                >
                    <div css={mobileDotsCss}>
                        {props.cards.map((card, index) => (
                            <button
                                key={`${card.title}-${index}-mobile-dot`}
                                onClick={() => {
                                    if (index !== selectedIndex)
                                        resetVideoAndSetSelectedIndex(index);
                                }}
                                className={
                                    index === selectedIndex ? "active" : ""
                                }
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeatureHighlight;
